"use client"

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { useRef } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  FileText,
  Calendar,
  MapPin,
  User,
  Shield,
  CheckCircle2,
  CircleDot,
  Clock,
  Scale,
  AlertTriangle,
  Printer,
  Bomb,
} from "lucide-react"
import { BoundDownOrder } from "./bound-down-order"
import type { Document } from "@/lib/interfaces"
import { useReactToPrint } from "react-to-print"

interface DocumentViewPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  document: Document | null
}

export function DocumentViewPopup({ open, onOpenChange, document }: DocumentViewPopupProps) {
  const printRef = useRef<HTMLDivElement>(null)
  const handlePrint = useReactToPrint({
    contentRef: printRef,  // Changed from 'content' to 'contentRef'
    documentTitle: `Document_${document?.fileName || "General_Diary"}`,
  })
  if (!document) return null

  const { generalDiary: gd, crpc129Notice, bNSL129Notice, crpcBondOrder } = document

  console.log(gd, 'generalDiary from dv');

  const statusConfig: Record<string, { icon: any; color: string }> = {
    completed: {
      icon: <CheckCircle2 className="w-5 h-5 text-green-600" />,
      color: "bg-green-50",
    },
    current: {
      icon: <CircleDot className="w-5 h-5 text-blue-600" />,
      color: "bg-blue-50",
    },
    pending: {
      icon: <Clock className="w-5 h-5 text-gray-500" />,
      color: "bg-gray-50",
    },
  }

  const availableTabs = [
    { id: "107", label: "GD Entry (107/126)", available: !!gd, data: gd },
    { id: "111", label: "Interim Notice (111/130)", available: !!crpc129Notice, data: crpc129Notice },
    { id: "116_3", label: "Interim Order/Bond (116_3/135)", available: !!bNSL129Notice, data: bNSL129Notice },
    { id: "117", label: "Bond Order (117/136)", available: !!crpcBondOrder, data: crpcBondOrder },
  ].filter((tab) => tab.available)

  const defaultTab = availableTabs[0]?.id || "107"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[90vh] bg-white flex flex-col w-[70%]" style={{ maxWidth: "1800px" }}>
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-md font-bold text-black flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Document Details - {document.fileName || gd?.gdNo}
            </DialogTitle>
            <Button 
              onClick={handlePrint}
              variant="outline"
              className="flex items-center gap-2 mr-8"
            >
              <Printer className="h-4 w-4" />
              Print PDF
            </Button>
          </div>
        </DialogHeader>
      <div className="overflow-auto px-4" ref={printRef}>
        <Card className="border border-gray-200 mb-4 flex-shrink-0">
          <CardHeader className="">
            <CardTitle className="text-sm text-black flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Status Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="">
                <p className="text-xs font-medium text-gray-600">107/126 - General Diary Entry</p>
                <p className="text-black text-xs overflow-hidden">
                  {document.generalDiary ? statusConfig["completed"].icon : statusConfig["pending"].icon}
                </p>
              </div>
              <div className="">
                <p className="text-xs font-medium text-gray-600">111/130 - Interim Notice</p>
                <p className="text-black text-xs overflow-hidden">
                  {document.crpc129Notice ? statusConfig["completed"].icon : statusConfig["pending"].icon}
                </p>
              </div>
              <div className="">
                <p className="text-xs font-medium text-gray-600">116_3/135_3 - Interim Order/Bond</p>
                <p className="text-black text-xs overflow-hidden">
                  {document.bNSL129Notice ? statusConfig["completed"].icon : statusConfig["pending"].icon}
                </p>
              </div>
              <div className="">
                <p className="text-xs font-medium text-gray-600">117/136 - Bond Order</p>
                <p className="text-black text-xs overflow-hidden">
                  {document.crpcBondOrder ? statusConfig["completed"].icon : statusConfig["pending"].icon}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue={defaultTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
            {availableTabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} disabled={!tab.available}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Tab Content for Status 107 - General Diary */}
          <TabsContent value="107" className="flex-1 min-h-0">
            <ScrollArea className="h-full pr-4">
              <div className="space-y-4 pb-4">
                {/* Document Info */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Document Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">File Name</p>
                        <p className="text-black text-xs overflow-hidden" title={document.fileName}>
                          {document.fileName || gd?.gdNo}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Upload Date</p>
                        <p className="text-gray-700 text-xs">{new Date(document.createdAt || gd?.createdAt)?.toLocaleString() ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">GD Type</p>
                        <Badge
                          className={gd?.gdType === "FIR" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}
                        >
                          {gd?.gdType}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Basic Information */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Basic Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">GD Number</p>
                        <p className="text-gray-700 text-xs font-mono">{gd?.gdNo || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">GD Date & Time</p>
                        <p className="text-gray-700 text-xs">{gd?.gdDateTime || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Case Type</p>
                        <Badge className="bg-orange-100 text-orange-800">{gd?.caseType || '-'}</Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">State</p>
                        <p className="text-gray-700 text-xs">{gd?.state || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">District Unit</p>
                        <p className="text-gray-700 text-xs">{gd?.districtUnit || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Police Station</p>
                        <p className="text-gray-700 text-xs">{gd?.policeStation?.name || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Officer Information */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Officer Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Entry For Officer</p>
                        <p className="text-gray-700 text-xs">{gd?.entryForOfficer || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Officer Name</p>
                        <p className="text-gray-700 text-xs">{gd?.officerName || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Information Type</p>
                        <p className="text-gray-700 text-xs">{gd?.informationType || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Under Section</p>
                        <p className="text-gray-700 text-xs">{gd?.underSection || '-'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Subject</p>
                      <p className="text-gray-700 text-xs">{gd?.subject || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Case Details */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Case Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Incident Description</p>
                      <p className="text-gray-700 text-xs">{gd?.incidentDescription || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Action Taken</p>
                      <p className="text-gray-700 text-xs">{gd?.actionTaken || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Categories */}
                {gd?.categories && gd.categories.length > 0 && (
                  <Card className="border border-gray-200">
                    <CardHeader className="">
                      <CardTitle className="text-sm text-black flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Categories
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">S.No</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>IPC Sections</TableHead>
                            <TableHead>BNS Sections</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {gd.categories.map((category, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium text-xs">{index + 1}</TableCell>
                              <TableCell className="text-xs">{category.caseCategory.title || '-'}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {category.caseCategory.ipcSections ? category.caseCategory.ipcSections.split(',').map((section: string) => (
                                    <Badge key={section} variant="secondary" className="bg-gray-100 text-gray-800 text-xs">
                                      {section}
                                    </Badge>
                                  )) : '-'}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {category.caseCategory.bnsSections ? category.caseCategory.bnsSections.split(',').map((section: string) => (
                                    <Badge key={section} variant="secondary" className="bg-gray-100 text-gray-800 text-xs">
                                      {section}
                                    </Badge>
                                  )) : '-'}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Questioner Responses */}
                <Card className="border border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Questioner Responses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {gd?.questionResponse && gd.questionResponse.length > 0 ? (
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {gd.questionResponse.map((response) => (
                          <div key={response.id} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-xs">
                            <span className="text-black truncate flex-1 pr-2">
                              {response.question.questionText}
                            </span>
                            <Badge 
                              className={`text-xs px-1.5 py-0.5 ${
                                response.answer 
                                  ? "bg-green-100 text-green-800 hover:bg-green-100" 
                                  : "bg-red-100 text-red-800 hover:bg-red-100"
                              }`}
                            >
                              {response.answer ? "हाँ" : "नहीं"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-3 text-gray-500 text-xs">
                        No questioner responses available.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Weapon details */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <Bomb className="h-5 w-5" />
                      Weapon Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {gd?.weapons && gd.weapons.length > 0 && gd.weapons.map((weapon, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <h4 className="font-medium text-black mb-2">Weapon {index + 1}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Type</p>
                              <p className="text-gray-700 text-xs">{weapon.type || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Model</p>
                              <p className="text-gray-700 text-xs">{weapon.model || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Serial Number</p>
                              <p className="text-gray-700 text-xs">{weapon.serialNumber || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">License Number</p>
                              <p className="text-gray-700 text-xs">{weapon.licenseNumber || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Recovered From</p>
                              <p className="text-gray-700 text-xs">{weapon.recoveredFrom || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Condition</p>
                              <p className="text-gray-700 text-xs">{weapon.condition || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Remarks</p>
                              <p className="text-gray-700 text-xs">{weapon.remarks || '-'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Seized</p>
                              <p className="text-gray-700 text-xs">{weapon.isSeized ? 'Yes' : 'No'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </CardContent>
                </Card>

                {/* Suspects */}
                {gd?.suspects && gd?.suspects.length > 0 && (
                  <Card className="border border-gray-200">
                    <CardHeader className="">
                      <CardTitle className="text-lg text-black">Suspects</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">S.No</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Father's Name</TableHead>
                            <TableHead>Address</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {gd?.suspects.map((suspect, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium text-xs">{index + 1}</TableCell>
                              <TableCell className="text-xs">{suspect.name || '-'}</TableCell>
                              <TableCell className="text-xs">{suspect.fatherName || '-'}</TableCell>
                              <TableCell className="text-xs">{suspect.address || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Victims */}
                {gd?.victims && gd?.victims.length > 0 && (
                  <Card className="border border-gray-200">
                    <CardHeader className="">
                      <CardTitle className="text-lg text-black">Victim/Opposing Party</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">S.No</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Father's Name</TableHead>
                            <TableHead>Address</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {gd?.victims.map((victim, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium text-xs">{index + 1}</TableCell>
                              <TableCell className="text-xs">{victim.name || '-'}</TableCell>
                              <TableCell className="text-xs">{victim.fatherName || '-'}</TableCell>
                              <TableCell className="text-xs">{victim.address || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Acts and Sections */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Acts and Sections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {gd && gd.actsAndSections && gd.actsAndSections.length > 0 && gd.actsAndSections.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Act</p>
                            <p className="text-gray-700 text-xs">{item.act || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Section</p>
                            <p className="text-gray-700 text-xs">{item.section || '-'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Report Information */}
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-lg text-black">Report Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Report Printed On</p>
                      <p className="text-gray-700 text-xs">{gd?.reportPrintedOn || '-'}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Printed By</p>
                        <p className="text-gray-700 text-xs">
                          {gd?.reportPrintedByName && gd?.reportPrintedByRank ? `${gd.reportPrintedByName} (${gd.reportPrintedByRank})` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Signature</p>
                        <p className="text-gray-700 text-xs">
                          {gd?.signatureName && gd?.signatureRank && gd?.signatureIdNo ? `${gd.signatureName} (${gd.signatureRank}) - ID: ${gd.signatureIdNo}` : '-'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tab Content for Status 111 - CRPC 129 Notice */}
          <TabsContent value="111" className="flex-1 min-h-0">
            <ScrollArea className="h-full pr-4">
              <div className="space-y-4 pb-4">
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <Scale className="h-5 w-5" />
                      CRPC 129 Notice Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Court Name</p>
                        <p className="text-gray-700 text-xs">{crpc129Notice?.courtName || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Case Number</p>
                        <p className="text-black font-mono">{crpc129Notice?.caseNumber || '-'}</p>
                      </div>
                      <div className="hidden">
                        <p className="text-sm font-medium text-gray-600">Issuing Officer</p>
                        <p className="text-gray-700 text-xs">{crpc129Notice?.issuingOfficer || '-'}</p>
                      </div>
                      <div className="hidden">
                        <p className="text-sm font-medium text-gray-600">Notice Type</p>
                        <Badge className="bg-purple-100 text-purple-800">{crpc129Notice?.noticeType || '-'}</Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 hidden">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Recipient Name</p>
                        <p className="text-gray-700 text-xs">{crpc129Notice?.recipientName || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Father's Name</p>
                        <p className="text-gray-700 text-xs">{crpc129Notice?.recipientFatherName || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Age</p>
                        <p className="text-gray-700 text-xs">{crpc129Notice?.recipientAge || '-'}</p>
                      </div>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Address</p>
                      <p className="text-gray-700 text-xs">{crpc129Notice?.recipientAddress || '-'}</p>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Notice Content</p>
                      <p className="text-black bg-gray-50 p-3 rounded-lg">{crpc129Notice?.noticeContent || '-'}</p>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Court Order</p>
                      <p className="text-black bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                        {crpc129Notice?.courtOrder || '-'}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 hidden">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Appearance Date</p>
                        <p className="text-gray-700 text-xs">
                          {crpc129Notice?.appearanceDate
                            ? new Date(crpc129Notice.appearanceDate).toLocaleDateString("en-IN")
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Appearance Time</p>
                        <p className="text-gray-700 text-xs">{crpc129Notice?.appearanceTime || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Issue Date</p>
                        <p className="text-gray-700 text-xs">
                          {crpc129Notice?.issueDate ? new Date(crpc129Notice.issueDate).toLocaleDateString("en-IN") : "-"}
                        </p>
                      </div>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Magistrate Signature</p>
                      <p className="text-gray-700 text-xs">{crpc129Notice?.magistrateSignature || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                {crpc129Notice?.boundDownOrder && <BoundDownOrder boundDownOrder={crpc129Notice.boundDownOrder} />}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tab Content for Status 116_3 - BNSL 129 Notice */}
          <TabsContent value="116_3" className="flex-1 min-h-0">
            <ScrollArea className="h-full pr-4">
              <div className="space-y-4 pb-4">
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      BNSL 129 Notice Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Case Number</p>
                        <p className="text-black font-mono">{bNSL129Notice?.caseNumber || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Section</p>
                        <Badge className="bg-red-100 text-red-800">{bNSL129Notice?.section || '-'}</Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Police Station</p>
                        <p className="text-gray-700 text-xs">{bNSL129Notice?.policeStation?.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">District</p>
                        <p className="text-gray-700 text-xs">{bNSL129Notice?.district || '-'}</p>
                      </div>
                      <div className="hidden">
                        <p className="text-sm font-medium text-gray-600">Court</p>
                        <p className="text-gray-700 text-xs">{bNSL129Notice?.court || '-'}</p>
                      </div>
                      <div className="hidden">
                        <p className="text-sm font-medium text-gray-600">Petitioner</p>
                        <p className="text-gray-700 text-xs">{bNSL129Notice?.petitioner || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Issuing Officer Name</p>
                        <p className="text-gray-700 text-xs">{bNSL129Notice?.issuingOfficerName || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Issuing Officer Rank</p>
                        <p className="text-gray-700 text-xs">{bNSL129Notice?.issuingOfficerRank || '-'}</p>
                      </div>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Description</p>
                      <p className="text-black bg-gray-50 p-3 rounded-lg">{bNSL129Notice?.description || '-'}</p>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Notice Subject</p>
                      <p className="text-black bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                        {bNSL129Notice?.noticeSubject || '-'}
                      </p>
                    </div>

                    {/* Respondents */}
                    {bNSL129Notice?.respondent && bNSL129Notice.respondent.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-3">Respondents</p>
                        <div className="space-y-2">
                          {bNSL129Notice.respondent.map((respondent:any, index:number) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-600">Name</p>
                                  <p className="text-gray-700 text-xs">{respondent.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-600">Father's Name</p>
                                  <p className="text-gray-700 text-xs">{respondent.father_name || '-'}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Report Sent Date</p>
                        <p className="text-gray-700 text-xs">
                          {bNSL129Notice?.reportDate ? new Date(bNSL129Notice.reportDate).toLocaleDateString("en-IN") : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Court Notice Date</p>
                        <p className="text-gray-700 text-xs">
                          {bNSL129Notice?.noticeIssueDate
                            ? new Date(bNSL129Notice.noticeIssueDate).toLocaleDateString("en-IN")
                            : "न्यायालय से प्राप्त नहीं"}
                        </p>
                      </div>
                    </div>

                    {bNSL129Notice?.courtAppearance && (
                      <div className="hidden">
                        <p className="text-sm font-medium text-gray-600 mb-2">Court Appearance</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Date</p>
                            <p className="text-gray-700 text-xs">
                              {bNSL129Notice.courtAppearance.date ? new Date(bNSL129Notice.courtAppearance.date).toLocaleDateString("en-IN") : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Time</p>
                            <p className="text-gray-700 text-xs">{bNSL129Notice.courtAppearance.time || '-'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Legal Action</p>
                      <p className="text-black bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
                        {bNSL129Notice?.legalAction || '-'}
                      </p>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Magistrate Signature</p>
                      <p className="text-gray-700 text-xs">{bNSL129Notice?.magistrateSignature || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                {bNSL129Notice?.boundDownOrder && <BoundDownOrder boundDownOrder={bNSL129Notice.boundDownOrder} />}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tab Content for Status 117 - CRPC Bond Order */}
          <TabsContent value="117" className="flex-1 min-h-0">
            <ScrollArea className="h-full pr-4">
              <div className="space-y-4 pb-4">
                <Card className="border border-gray-200">
                  <CardHeader className="">
                    <CardTitle className="text-sm text-black flex items-center gap-2">
                      <Scale className="h-5 w-5" />
                      CRPC Bond Order Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Case Number</p>
                        <p className="text-black font-mono">{crpcBondOrder?.caseNumber || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Date of Order</p>
                        <p className="text-gray-700 text-xs">
                          {crpcBondOrder?.dateOfOrder
                            ? new Date(crpcBondOrder.dateOfOrder).toLocaleDateString("en-IN")
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Police Station</p>
                        <p className="text-gray-700 text-xs">{crpcBondOrder?.policeStation?.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">District</p>
                        <p className="text-gray-700 text-xs">{crpcBondOrder?.district || '-'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-600">Court</p>
                      <p className="text-gray-700 text-xs">{crpcBondOrder?.court || '-'}</p>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Sections Applied</p>
                      <Badge className="bg-indigo-100 text-indigo-800">{crpcBondOrder?.sectionsApplied || '-'}</Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 hidden">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Village</p>
                        <p className="text-gray-700 text-xs">{crpcBondOrder?.village || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Police Station Area</p>
                        <p className="text-gray-700 text-xs">{crpcBondOrder?.policeStationArea || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">District Area</p>
                        <p className="text-gray-700 text-xs">{crpcBondOrder?.districtArea || '-'}</p>
                      </div>
                    </div>

                    <div className="hidden">
                      <p className="text-sm font-medium text-gray-600">Description</p>
                      <p className="text-black bg-gray-50 p-3 rounded-lg">{crpcBondOrder?.description || '-'}</p>
                    </div>

                    {/* Accused List */}
                    {crpcBondOrder?.accusedList && crpcBondOrder.accusedList.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-3">Accused List</p>
                        <div className="space-y-2">
                          {crpcBondOrder.accusedList.map((accused:any, index:number) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-600">Name</p>
                                  <p className="text-gray-700 text-xs">{accused.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-600">Father's Name</p>
                                  <p className="text-gray-700 text-xs">{accused.fatherName || '-'}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {crpcBondOrder?.boundDownOrder && <BoundDownOrder boundDownOrder={crpcBondOrder.boundDownOrder} />}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
        </div>

        <div className="flex-shrink-0 flex justify-end pt-4 border-t border-gray-200 bg-white">
          <Button onClick={() => onOpenChange(false)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
